import styles from './styles.module.css'
import { useTranslation } from 'next-i18next'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import Textarea from '@/components/ui/textarea'
import Upload, { UPLOAD_TYPES } from '@/components/ui/upload'
import Error from '@/components/ui/error'
import CustomButton, { BUTTON_SIZES, BUTTON_TYPES } from '@/components/ui/button'
import FooterButtonTabs from '@/components/ui/footer-button-tabs'
import InputClearIcon from '@/components/ui/icons/icons/InputClearIcon'

const FILE = { EXT: 'jpg,jpeg,png,doc,pdf,gif,zip,rar,tar,txt,xls,docx,xlsx,odt', SIZE: 2000 }
const ACCEPT = '.jpg,.jpeg,.png,.doc,.pdf,.gif,.zip,.rar,.tar,.txt,.xls,.docx,.xlsx,.odt'

const Form = ({ onClose, onSubmit }) => {
  const { t } = useTranslation(['common', 'objectWrite'])
  const { isMessageSentLoading, messageSentError } = useSelector(({ messages }) => messages)

  const validationSchema = Yup.object().shape({
    message: Yup.string().required(t('validation::required')),
    file: Yup.mixed()
      .file()
      .fileExt(FILE.EXT, t('validation::fileExt', { ext: FILE.EXT }))
      .fileSize(FILE.SIZE, t('validation::fileSize', { size: '2Mb' })),
  })

  return (
    <Formik
      initialValues={{
        message: '',
        file: null,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, resetForm }) => (
        <form onSubmit={handleSubmit}>
          <div className={styles.writeMessageInputs}>
            <div className={styles.writeMessageArea}>
              <Textarea
                name="message"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.message}
                maxWidth="776px"
                minWidth="100%"
                minHeight="180px"
                maxHeight="400px"
              />
              <div className={styles.writeMessageFileRow}>
                <div className={styles.writeMessageFile}>
                  {values.file !== null && values.file instanceof File && (
                    <div className={styles.writeMessageFileName}>{values.file.name}</div>
                  )}
                  <Upload
                    name="file"
                    setFieldValue={setFieldValue}
                    onRemove={() => {
                      setFieldValue('file', null)
                    }}
                    label={t('objectWrite::addFile')}
                    removeLabel={t('objectWrite::removeFile')}
                    value={values.file}
                    options={{
                      accept: ACCEPT,
                    }}
                    type={UPLOAD_TYPES.SMALL}
                    errorMessage={errors.file}
                  />
                </div>
                <div className={styles.writeMessageFileText}>
                  <div className={styles.writeMessageFileTextTop}>
                    {t('objectWrite::fileFormat')}
                  </div>
                  <div className={styles.writeMessageFileTextBottom}>
                    {t('objectWrite::fileSize')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.writeButtonsRow}>
            <div
              className={styles.writeClear}
              onClick={() => {
                resetForm()
                onClose()
              }}
            >
              <div className={styles.writeCross}>
                <InputClearIcon />
              </div>
              <div className={styles.writeText}>{t('objectWrite::clear')}</div>
            </div>
            <div className={styles.writeSend}>
              <CustomButton
                text={t('common::button::sendMessage')}
                size={BUTTON_SIZES.FLAT}
                type={BUTTON_TYPES.BLUE}
                isSubmit
                disabled={isMessageSentLoading}
              />
            </div>
          </div>
          <div className={styles.writeButtonsRowMobile}>
            {messageSentError && <Error isOnButton message={messageSentError} />}
            <FooterButtonTabs
              isHiddenCount={true}
              rightLabel={t('common::button::send')}
              leftLabel={t('common::button::clearSelect')}
              onActionLeft={onClose}
            />
          </div>
          {messageSentError && (
            <div className={styles.errorWrapper}>
              <Error isOnButton message={messageSentError} />
            </div>
          )}
        </form>
      )}
    </Formik>
  )
}

export default Form
